import Raven from 'raven-js';
import ActionType from './types';
import axios from 'axios';
import qs from 'qs';
import { browserHistory } from 'react-router';

import store from './../store/configureStore';
import * as Constants from './../constants';
import { makeSignedRequest, getSignedRequest, userClubMemberId, performRequest,
  logException, logFailure, convertSecTimeStampToMiliSec, handleErrors, getCentroidFromGeoJson, checkIsBookingOpen } from './../utils/';
import { getUserAccommodationInfoURL, getCurrentStateURL, getGeographyURL } from './../utils/restUrls';
import { setGeoJson } from './geoJson';
import { setCheckoutDetailsIfSpotsHeld } from './checkout';
import { getProcessedSpotsList } from './parkingSpots';

export function getRequestToken() {

  const options = { oauth_callback: Constants.CALLBACK_URL };

  return (dispatch) => {
    getSignedRequest('POST', Constants.REQUEST_TOKEN_URL, options)
      .then((signedRequest) => {
        // by default the content-type of request is 'application/x-www-form-urlencoded'
        axios.post(Constants.REQUEST_TOKEN_URL, qs.stringify(signedRequest))
          .then((response) => {
            if (response.status === 200) {
              return qs.parse(response.data);
            } else {
              logFailure('Fetch Request Token Failed', response);
              throw new Error('Something went wrong');
            }
          })
          .then((body) => {
            const { oauth_callback_confirmed, oauth_token: oauthToken, oauth_token_secret: oauthTokenSecret } = body;

            if (oauth_callback_confirmed === "true") {
              dispatch({
                type: ActionType.STORE_REQUEST_TOKEN,
                oauthToken,
                oauthTokenSecret,
              });
            }
          })
          .then(() => window.location = `${Constants.AUTHORIZATION_URL}?oauth_token=${store.getState().user.oauthToken}`)
          .catch(error => {
            logException(error, {});
          });
      });
  };
}

export function getAccessToken(oauth_verifier, callback) {

  const options = {
    oauth_verifier: oauth_verifier,
    oauth_token: store.getState().user.oauthToken,
  };

  return (dispatch) => {
    getSignedRequest('POST', Constants.ACCESS_TOKEN_URL, options)
      .then((signedRequest) => {
        // by default the content-type of request is 'application/x-www-form-urlencoded'
        axios.post(Constants.ACCESS_TOKEN_URL, qs.stringify(signedRequest))
          .then((response) => {
            if (response.status === 200) {
              return qs.parse(response.data);
            } else {
              logFailure('Fetch Access Token Failed', response);
              throw new Error('Something went wrong');
            }
          })
          .then((body) => {
            const { oauth_token: oauthToken, oauth_token_secret: oauthTokenSecret, profileId } = body;
            dispatch({
              type: ActionType.STORE_ACCESS_TOKEN,
              oauthToken,
              oauthTokenSecret,
              profileId,
            });
            callback && callback();
          })
          .then(() => browserHistory.push('/parking'))
          .catch(error => {
            logException(error, {});
          });
      });
  };
}

export const setUserInfo = () => {
  return (dispatch) => (
    makeSignedRequest(Constants.USER_INFO_URL)
      .then(body => {
        dispatch( {
          type: ActionType.SET_USER_INFO,
          ...body.response.profile
        });
      })
  );
};

export const setCurrentStateForSpots = () => {
  const url = getCurrentStateURL();

  return (dispatch) => (
    performRequest(url)
      .then(res => {
        if(res && res.success){
          //Data is coming in jsonstring. Parsing jsonstring into json.
          const list = JSON.parse(res.data);
          const processedSpotsList = getProcessedSpotsList(list);
          dispatch({
            type: ActionType.SET_PARKING_SPOT_DETAILS,
            list: processedSpotsList,
          });
        }
      })
  );
};

export const setUserAccommodationInfo = () => {
  const uidClubMember = userClubMemberId();
  const url = getUserAccommodationInfoURL(uidClubMember);
  if(uidClubMember) {
    return (dispatch) => (
      makeSignedRequest(url, 'GET', {}, {}, {ignore_error_codes: [404]})
        .then(body => {
          const { list } = store.getState().parkingSpots;
          const reservedSpotsInApiResponse = body.data.reservedSpots;

          const reservedSpots = [];
          let heldSpot = null;
          let heldExpirationTS = null;

          reservedSpotsInApiResponse.forEach((spotId) => {
            const { status, expirationTS } = list[spotId];
            switch (status) {
              case 'HELD':
                heldSpot = spotId;
                heldExpirationTS = expirationTS;
                break;
              default:
                reservedSpots.push(spotId);
            }
          });

          if(heldExpirationTS){
            dispatch({
              type: ActionType.UPDATE_TIMESTAMP,
              timeStamp: convertSecTimeStampToMiliSec(heldExpirationTS),
            });
          }

          dispatch({
            type: ActionType.SET_USER_ACCOMMODATION_INFO,
            ...body.data,
            heldSpot,
            reservedSpots
          });

          dispatch(setCheckoutDetailsIfSpotsHeld());
        })
        .catch(error => {
          dispatch({type: ActionType.SET_DEFAULT_ACCOMMODATION_INFO});
        })
    );
  }else {
    return (dispatch) => {
      dispatch({type: ActionType.SET_DEFAULT_ACCOMMODATION_INFO});
    };
  }
};

export const setGeography = () => {
  const url = getGeographyURL();

  return (dispatch) => (
    performRequest(url)
      .then(body => {
        if(body && body.success) {
          const { clubName, dtmClose, dtmOpen, eventName, isOpen, jsnAccommodationMap } = body.data;
          const mapGeoJson = JSON.parse(jsnAccommodationMap);
          dispatch({
            type: ActionType.ADD_MAP_GEO_JSON,
            mapGeoJson,
          });
          dispatch(setGeoJson(mapGeoJson));
          dispatch({
            type: ActionType.UPDATE_MAP_ACCOMMODATION_INFO,
            clubName, eventName, isOpen,
            dtmOpen: convertSecTimeStampToMiliSec(dtmOpen),
            dtmClose: convertSecTimeStampToMiliSec(dtmClose),
          });
        }
      })
  );
};

export const initializeDefaultStates = () => {
  return (dispatch) => {
    dispatch(setGeography());
    dispatch(setCurrentStateForSpots());
    dispatch({
      type: ActionType.SET_DEFAULT_STATE_TIMESTAMP,
      parkingDataFetchedAt: new Date().getTime()
    });
  };
};

export const initializeUserStates = () => {
  return (dispatch) => {
    dispatch(setUserInfo())
      .then(() => {
        dispatch(setUserAccommodationInfo());
      });

    dispatch({
      type: ActionType.SET_USER_DATA_FETCH_TIMESTAMP,
      userDataFetchedAt: new Date().getTime()
    });
  };
};

export const updateIsBookingOpen = (dtmOpen, dtmClose) => {
  return (dispatch) => {
    const bookingOpenInfo = checkIsBookingOpen(dtmOpen, dtmClose);
    dispatch({
      type: ActionType.UPDATE_BOOKING_OPEN_INFO,
      ...bookingOpenInfo
    });
  };
};



// WEBPACK FOOTER //
// ./src/actions/index.js